import React, { memo, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { CircularProgress } from "@mui/material";
import {
	Chart as ChartJS,
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
	PointElement,
	LineElement,
	LineController,
} from "chart.js";

import "./CIIGraph.scss";

import { roundOff } from "../../../../../../services/utilities";
import axiosHttp from "../../../../../../services/interceptor";

ChartJS.register(
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
	PointElement,
	LineElement,
	LineController
);

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CIIGraph = ({
	ChildReportMstId,
	isCalculatorPage,
	CIICalculatedData,
	showGraph,
}) => {
	const [ciiGraphData, setCiiGraphData] = useState({});
	const [showCIIGraph, setShowCIIGraph] = useState();
	const [errorMessage, setErrorMessage] = useState(""); // if imovesseltype is passenger, have to show error message (handling in backend)
	const [loader, setLoader] = useState(false);

	const getCiiGraphData = async () => {
		setLoader(true);
		await axiosHttp({
			url: `${BASE_URL}api/Cii/CarbonIntensityIndicator/${ChildReportMstId}`,
			method: "GET",
		})
			.then((res) => {
				if (res?.data?.isSuccess) {
					setShowCIIGraph(true);
					setCiiGraphData(res?.data?.data);
					setLoader(false);
				} else {
					setShowCIIGraph(false);
					setLoader(false);
					setErrorMessage(res?.data?.errorMessage);
				}
			})
			.catch((err) => {
				setLoader(false);
				console.error(err);
			});
	};

	useEffect(() => {
		setShowCIIGraph(showGraph);
	}, [showGraph]);

	useEffect(() => {
		if (!isCalculatorPage) {
			getCiiGraphData();
		}
	}, [ChildReportMstId]);

	useEffect(() => {
		if (isCalculatorPage) {
			if (CIICalculatedData) {
				setCiiGraphData(CIICalculatedData);
			}
		}
	}, [CIICalculatedData]);

	const barCharts = [
		{
			label: "",
			data: ciiGraphData?.aValuesPerYear
				? Object.values(ciiGraphData.aValuesPerYear).map((value) =>
						roundOff(value, 2, 2)
				  )
				: {},
			backgroundColor: ["green", "green", "green", "green"],
			stack: "stack1",
			stacked: true,
			type: "bar",
			yAxisID: "y-axis-1",
			order: 4,
		},
		{
			label: "",
			data: ciiGraphData?.bValuesPerYear
				? Object.values(ciiGraphData.bValuesPerYear).map((value) =>
						roundOff(value, 2, 2)
				  )
				: {},
			backgroundColor: [
				"lightgreen",
				"lightgreen",
				"lightgreen",
				"lightgreen",
			],
			stack: "stack1",
			stacked: true,
			type: "bar",
			yAxisID: "y-axis-1",
			order: 5,
		},
		{
			label: "",
			data: ciiGraphData?.cValuesPerYear
				? Object.values(ciiGraphData.cValuesPerYear).map((value) =>
						roundOff(value, 2, 2)
				  )
				: {},
			backgroundColor: ["yellow", "yellow", "yellow", "yellow"],
			stack: "stack1",
			stacked: true,
			type: "bar",
			yAxisID: "y-axis-1",
			order: 6,
		},
		{
			label: "",
			data: ciiGraphData?.dValuesPerYear
				? Object.values(ciiGraphData.dValuesPerYear).map((value) =>
						roundOff(value, 2, 2)
				  )
				: {},
			backgroundColor: ["orange", "orange", "orange", "orange"],
			stack: "stack1",
			stacked: true,
			type: "bar",
			yAxisID: "y-axis-1",
			order: 7,
		},
		{
			label: "",
			// data: Object.values(ciiGraphData.eValuesPerYear || {}),
			data: ciiGraphData?.eValuesPerYear
				? Object.values(ciiGraphData.eValuesPerYear).map((value) =>
						roundOff(value, 2, 2)
				  )
				: {},
			backgroundColor: ["red", "red", "red", "red"],
			stack: "stack1",
			stacked: true,
			type: "bar",
			yAxisID: "y-axis-1",
			order: 8,
		},
	];

	const lineCharts = [
		{
			label: "Required CII",
			data: ciiGraphData?.requiredCiiPerYear
				? Object.values(ciiGraphData.requiredCiiPerYear).map((value) =>
						roundOff(value ?? 0, 3, 3)
				  )
				: {},
			type: "line",
			borderColor: "#000",
			pointBackgroundColor: "#fff",
			tension: 0.4,
			fill: false,
			stacked: false,
			order: 2,
		},
		{
			label: "Attained CII w/o exclusions",
			data: ciiGraphData?.ciiWithoutExclusionsPerYear
				? Object.values(ciiGraphData.ciiWithoutExclusionsPerYear).map(
						(value) => roundOff(value, 3, 3)
				  )
				: {},
			backgroundColor: "rgba(0, 0, 255, 0.2)",
			borderColor: "blue",
			type: "line",
			borderDash: [10, 5],
			tension: 0.4,
			fill: false,
			stacked: false,
			order: 3,
		},
		{
			label: "Attained CII with exclusions",
			data: ciiGraphData.ciiWithExclusionsPerYear
				? Object.values(ciiGraphData.ciiWithExclusionsPerYear).map(
						(value) => roundOff(value, 3, 3)
				  )
				: {},
			backgroundColor: "rgba(255, 165, 0, 1)",
			borderColor: "orange",
			type: "line",
			borderDash: [10, 5],
			tension: 0.4,
			fill: false,
			stacked: false,
			order: 1,
		},
	];

	const data = {
		labels: Object.keys(ciiGraphData?.requiredCiiPerYear || {}),
		datasets: [...lineCharts, ...barCharts],
	};

	const options = {
		responsive: true,
		plugins: {
			tooltip: {
				callbacks: {
					label: function (context) {
						const value = Number(context.raw);

						if (context.dataset.label === "Required CII") {
							return `${context.dataset.label}: ${value.toFixed(3)}`;
						}
						if (context.dataset.label === "Attained CII w/o exclusions" || context.dataset.label === "Attained CII with exclusions") {
							return `${context.dataset.label}: ${value.toFixed(3)}`;
						}
						return `${context.dataset.label}: ${value}`;
					},
				},
			},
			legend: {
				display: true,
				position: "bottom",
				labels: {
					filter: (item) => {
						return item.text === "Required CII" || item.text === "Attained CII w/o exclusions" || item.text === "Attained CII with exclusions";
					},
				},
			},
			title: {
				display: true,
				text: "Preliminary CII Rating",
			},
			datalabels: false,
		},
		scales: {
			x: {
				stacked: true,
				title: {
					display: true,
					text: "Year",
				},
				grid: {
					display: true,
					offset: true,
					color: "darkgrey",
					position: "front",
				},
				barPercentage: 0.4,
				barThickness: 6,
			},
			"y-axis-1": {
				title: {
					display: true,
					text: "CII Rating",
				},
				ticks: {
					beginAtZero: true,
					min: 0,
				},
				grid: {
					display: false,
					offset: false,
				},
			},
		},
	};

	const grades = [
		{ grade: "E", color: "red" },
		{ grade: "D", color: "orange" },
		{ grade: "C", color: "yellow" },
		{ grade: "B", color: "lightgreen" },
		{ grade: "A", color: "green" },
	];

	const getBadgeStyle = (grade) => {
		const gradeInfo = grades.find((g) => g.grade === grade);
		return gradeInfo ? { backgroundColor: gradeInfo.color } : {};
	};

	let reportedData = [
		{
			label: "REPORT YEAR",
			value: ciiGraphData?.reportYear,
			className: "fw-bold",
		},
		{
			label: "ATTAINED CII W/O EXCLUSIONS**",
			value:
				ciiGraphData?.attainedCIIWithoutExclusion == -1
					? "Not Applicable"
					: ciiGraphData?.attainedCIIWithoutExclusion != null && ciiGraphData?.attainedCIIWithoutExclusion !== undefined
					? roundOff(ciiGraphData?.attainedCIIWithoutExclusion, 3, 3)
					: 0.000,
			className: "fw-bold",
		},
		{
			label: "ATTAINED CII WITH EXCLUSIONS**",
			value:
				ciiGraphData?.attainedCIIWithExclusion == -1
					? "Not Applicable"
					: ciiGraphData?.attainedCIIWithExclusion != null && ciiGraphData?.attainedCIIWithExclusion !== undefined
					? roundOff(ciiGraphData?.attainedCIIWithExclusion, 3, 3)
					: 0.000,
			className: "fw-bold",
		},
		{
			label: "REQUIRED CII",
			value:
				ciiGraphData?.attainedRequiredCII == -1
					? "Not Applicable"
					: ciiGraphData?.attainedRequiredCII != null && ciiGraphData?.attainedRequiredCII !== undefined
					? roundOff(ciiGraphData?.attainedRequiredCII, 3, 3)
					: 0.000,
			className: "fw-bold",
		},
		{
			label: "CII RATING W/O EXCLUSIONS",
			value: ciiGraphData?.attainedCIIGradeWithoutExclusion,
			className: "badge fw-bold",
			style: getBadgeStyle(
				ciiGraphData?.attainedCIIGradeWithoutExclusion
			),
		},
		{
			label: "CII RATING WITH EXCLUSIONS",
			value: ciiGraphData?.attainedCIIGradeWithExclusion
				? ciiGraphData?.attainedCIIGradeWithExclusion
				: ciiGraphData?.attainedCIIGradeWithoutExclusion,
			className: "badge fw-bold",
			style: getBadgeStyle(ciiGraphData?.attainedCIIGradeWithExclusion),
		},
	];
	if (isCalculatorPage) {
		reportedData = reportedData.filter(
			(report) =>
				report.label !== "CII RATING WITH EXCLUSIONS" &&
				report.label !== "ATTAINED CII WITH EXCLUSIONS**"
		);
	}

	return (
		<>
			{loader && (
				<span className="cntr">
					<CircularProgress size={40} />
				</span>
			)}{" "}
			{showCIIGraph ? (
				<div
					className={
						isCalculatorPage
							? "cii-graph-container-for-dashboard"
							: "cii-graph-container mt-5"
					}
				>
					<div className="legend-bar mt-5">
						<div>
							<h6 className="mb-1">
								<b>Legend</b>
							</h6>
							{grades.map((gradeInfo, index) => (
								<div
									key={`legend-${index}`}
									className="grade"
									style={{
										backgroundColor: gradeInfo.color,
									}}
								>
									{gradeInfo.grade}
								</div>
							))}
						</div>
						<div className="chart-layout">
							<Bar data={data} options={options} />
						</div>
					</div>
					<div className="d-flex justify-content-center align-items-center">
						<div
							className={
								"mt-2 reported-data-container reported-data-item " +
								(isCalculatorPage
									? "cii-calculator-container"
									: "")
							}
						>
							{reportedData.map((item, index) => (
								<div
									key={`report-data-${index}`}
									className="d-flex justify-content-between reported-data-item"
								>
									<div className="reported-data-label">
										{item.label}
									</div>
									<div
										className={item.className || ""}
										style={item.style}
									>
										{item.value}
									</div>
								</div>
							))}
							<div className="d-flex justify-content-end mt-4 pt-2">
								<div
									className={
										"label-units " +
										(isCalculatorPage
											? "cii-calculator-label-units"
											: "text-dark")
									}
								>
									CII units: gCO2/dwt.nmile
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<div className="cii-error-message text-center mt-5 pt-5">
					{errorMessage}
				</div>
			)}
		</>
	);
};

export default memo(CIIGraph);
